import { useAuth } from '@/contexts/auth-context';
import { useBillingActionScope } from '@/hooks/actions/use-billing-action-scope';
import api from '@/libs/axiosInstance';
import { readBillingContext } from '@/libs/billing-access';
import type { ApiResponse, OrganizationBillingContext } from '@fluenceur/types';
import { useQuery } from '@tanstack/react-query';

export const BILLING_CONTEXT_QUERY_KEY = ['billingContext'] as const;

export const billingContextQueryKey = (scope: {
  userId: string | null;
  organizationId: string | null;
  brandId: string | null;
}) => [...BILLING_CONTEXT_QUERY_KEY, scope.userId, scope.organizationId, scope.brandId] as const;

export function useBillingContextQuery({ enabled = true }: { enabled?: boolean } = {}) {
  const { isLoading } = useAuth();
  const { scope, assertCurrent } = useBillingActionScope();
  const canRead = !!scope.userId && !!scope.brandId && !!scope.organizationId;

  return useQuery({
    queryKey: billingContextQueryKey(scope),
    enabled: enabled && !isLoading && canRead,
    queryFn: async ({ signal }): Promise<OrganizationBillingContext> => {
      assertCurrent(scope);
      if (!canRead) throw new Error('No authorized billing context');
      const response = await api.get<ApiResponse<unknown>>('/dashboard/billing/context', {
        signal,
      });
      assertCurrent(scope);
      const context = readBillingContext(response.data.data);
      if (!response.data.success || !context) {
        throw new Error('Failed to read billing access');
      }
      if (context.brandId !== scope.brandId || context.organizationId !== scope.organizationId) {
        throw new Error('Billing context does not match the current organization and brand');
      }
      return context;
    },
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    retry: 1,
  });
}
